import { Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { BrokerRoot } from '@components/routing/BrokerRoot';
import { ProtectedRoute } from '@components/routing/ProtectedRoute';
import { DashboardRoute } from '@components/routing/DashboardRoute';
import { SignupWizardLayout } from '@components/routing/SignupWizardLayout';
import { SignupSessionGuard } from '@components/routing/SignupSessionGuard';
import { BrokerRootRedirect, BrokerLangOnlyRedirect } from './routes/BrokerEntryRoutes';
import { AuthLoadingOverlay } from '@components/AuthLoadingOverlay';
import { LoadingBlock } from '@components/LoadingBlock';
import {
  RegisterRoute,
  SignUpOtpRoute,
  SignUpCompleteRoute,
  AuthNafathRoute,
  AuthAddressRoute,
  AuthSuccessRoute,
} from './routes/signup';
import {
  MainBrokerageRoute,
  SubBrokerageRoute,
  WalletRoute,
  BrokerSettingsRoute,
  NewBrokerageRoute,
  LottieRoute,
  AccessDeniedRoute,
  DealSlugRoute,
} from './routes/broker';

import { PayerFlow } from './pages/payer/PayerFlow';
import { PayDevRedirectPage } from './pages/PayDevRedirectPage';
import { PayShortRedirectPage } from './pages/PayShortRedirectPage';
import { BankSimulatorPage } from './pages/BankSimulatorPage';

const LoadingFallback = () => <LoadingBlock className="min-h-screen" />;

/**
 * Root layout: renders AuthLoadingOverlay (path-aware startup preloader) over all routes,
 * then the matched route via Outlet.
 */
function RootLayout() {
  return (
    <>
      <AuthLoadingOverlay />
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      /*
       * Payer + dev tools come before /:lang so paths like /pay-dev and /bank-simulator
       * are never treated as a language code by BrokerRoot.
       */
      { path: '/pay/:dealId', element: <PayerFlow /> },
      { path: '/p/:code', element: <PayShortRedirectPage /> },
      { path: '/pay-dev', element: <PayDevRedirectPage /> },
      { path: '/bank-simulator', element: <BankSimulatorPage /> },
      { path: '/', element: <BrokerRootRedirect /> },
      {
        path: '/:lang',
        element: <BrokerRoot />,
        children: [
          { index: true, element: <BrokerLangOnlyRedirect /> },
          /* Signup wizard ————————————————————————————————————— */
          {
            element: <SignupWizardLayout />,
            children: [
              { path: 'signup', element: <RegisterRoute /> },
              { path: 'signup-verify', element: <SignUpOtpRoute /> },
              {
                path: 'signup-complete',
                element: <SignupSessionGuard><SignUpCompleteRoute /></SignupSessionGuard>,
              },
              {
                path: 'nafath-verification',
                element: <SignupSessionGuard><AuthNafathRoute /></SignupSessionGuard>,
              },
              {
                path: 'select-national-address',
                element: <SignupSessionGuard><AuthAddressRoute /></SignupSessionGuard>,
              },
              {
                path: 'success',
                element: <SignupSessionGuard><AuthSuccessRoute /></SignupSessionGuard>,
              },
            ],
          },
          /* Protected broker dashboard ————————————————————————— */
          {
            element: <ProtectedRoute />,
            children: [
              {
                element: <DashboardRoute />,
                children: [
                  { path: 'main-brokerage', element: <MainBrokerageRoute /> },
                  { path: 'sub-brokerage', element: <SubBrokerageRoute /> },
                  { path: 'wallet', element: <WalletRoute /> },
                  { path: 'settings', element: <BrokerSettingsRoute /> },
                  { path: 'new-brokerage', element: <NewBrokerageRoute /> },
                  { path: 'lottie', element: <LottieRoute /> },
                  { path: 'forbidden', element: <AccessDeniedRoute /> },
                  { path: 'access-denied', element: <AccessDeniedRoute /> },
                  /* Deal slug catch-all — must be last */
                  { path: ':slug', element: <DealSlugRoute /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <BrokerRootRedirect /> },
    ],
  },
]);
